/**
 * rebuy-cart-bridge.js — Londontown — v2.2 (2026-09-17)
 * ============================================================================
 * PURPOSE (unchanged from v1)
 *   Guarantee an add-to-cart is never silent: show Smart Cart when it's ready,
 *   otherwise fall back to the theme's native drawer.
 *
 * WHAT CHANGED IN v2 — and why
 *   v1 was correct about *what* to show, but it could fire the same cart sync
 *   several times for one shopper action. Under Cloudflare that read as bot-like
 *   burst traffic. Four specific defects are fixed:
 *
 *   1) UN-COALESCED READINESS WAITS (the main bug).
 *      v1's readiness poll ran up to 1500ms. Its 150ms debounce only collapsed
 *      events arriving inside that 150ms, so any cart event arriving later while
 *      Smart Cart was still initialising started ANOTHER concurrent poll loop.
 *      N events -> N loops -> N cart syncs.
 *      v2: at most ONE readiness wait exists at any time (`waiting`). Later
 *      events join it instead of starting their own.
 *
 *   2) SELF-FEEDING EVENT LOOP.
 *      v1's native fallback dispatched `cart:build` — which v1 itself listened
 *      for. It was guarded only by a 300ms flag that did not stop already-running
 *      loops. v2 no longer listens for `cart:build` at all (every dispatcher of
 *      it in this theme also dispatches `cart:refresh`, so no coverage is lost),
 *      AND suppresses our own dispatches synchronously as belt-and-braces.
 *
 *   3) NO RATE LIMIT ON THE NETWORK CALL.
 *      v2 separates UI from network:
 *        - showCart()  = UI only. Never throttled, so the cart always appears
 *                        (the never-silent guarantee is preserved exactly).
 *        - syncCart()  = the /cart.js-generating call. Throttled to at most one
 *                        per SYNC_COOLDOWN_MS, with a trailing-edge sync so the
 *                        final cart state is never dropped.
 *
 *   4) RETRY STORM AGAINST A FAILING/CHALLENGED ENDPOINT.
 *      If Rebuy can't initialise (e.g. its API is being challenged or is down),
 *      v1 would start a fresh 1500ms poll for every subsequent cart event.
 *      v2 backs off: after one readiness timeout it stops polling for
 *      SDK_BACKOFF_MS and goes straight to the native drawer. It degrades
 *      quietly instead of amplifying.
 *
 *   5) DEBOUNCE COULD STARVE THE REVEAL.
 *      A pure debounce delays the reveal for as long as events keep arriving.
 *      v2 adds a max-wait so the cart is always revealed within
 *      MAX_DEBOUNCE_WAIT of the first event in a burst.
 *
 * WHAT CHANGED IN v2.2
 *
 *   6) SMART CART OFF WAS TREATED AS SMART CART LATE.
 *      With Smart Cart disabled in the Rebuy dashboard the SDK loads and fires
 *      cart events, but Rebuy.SmartCart never exists. v2.1 read that as "not
 *      ready yet" and burned a 1500ms poll before every native fallback, then
 *      sat in a 30s backoff. v2.2 checks presence, not just readiness, and goes
 *      native immediately. The cart-icon handler no longer swallows the native
 *      click in that configuration either.
 *
 *      Also: Rebuy's widget buttons add through Rebuy's own handler and dispatch
 *      'rebuy:cart.add' / 'rebuy.add', never the theme's events. Those adds were
 *      silent because nothing was listening. Both are now wired in.
 *
 * Uses only documented Rebuy APIs: Rebuy.SmartCart.show()/.status,
 * Rebuy.Cart.fetchCart(). Safe when Rebuy is absent — every branch falls
 * through to the native drawer.
 * ============================================================================
 */
(function () {
  'use strict';

  var DEBOUNCE_MS       = 250;    // collapse the events a single interaction emits
  var MAX_DEBOUNCE_WAIT = 600;    // ...but never delay revealing the cart longer than this
  var SYNC_COOLDOWN_MS  = 1000;   // hard floor between cart syncs (network calls)
  var READY_TIMEOUT_MS  = 1500;   // how long to wait for Smart Cart before native fallback
  var READY_POLL_MS     = 150;
  var SDK_BACKOFF_MS    = 30000;  // after a readiness timeout, stop polling for this long

  var debounceTimer   = null;
  var burstStartedAt  = 0;      // (5) debounce max-wait, so a long click-stream can't starve the reveal
  var waiting         = false;  // (1) single in-flight readiness wait
  var selfDispatch    = false;  // (2) ignore events we dispatch ourselves
  var lastSyncAt      = 0;      // (3) throttle state
  var trailingTimer   = null;
  var backoffUntil    = 0;      // (4) quiet period after the SDK failed to become ready

  window.__rebuyCartBridgeVersion = '2.2'; // verifiable in production console

  function now() { return Date.now(); }
  function rebuyPresent() { return !!window.Rebuy; }
  // (6) Smart Cart is optional. When it is switched off in the Rebuy dashboard the
  // SDK still loads and still fires cart events, but never exposes Rebuy.SmartCart.
  // Waiting on readiness in that configuration can only ever time out, so every
  // reveal path checks presence first and goes native immediately instead.
  function smartCartPresent() {
    try { return !!(window.Rebuy && window.Rebuy.SmartCart); }
    catch (e) { return false; }
  }
  function smartCartReady() {
    try {
      return !!(window.Rebuy && window.Rebuy.SmartCart && window.Rebuy.SmartCart.status === 'ready');
    } catch (e) { return false; }
  }

  // ---- network: throttled, with trailing edge so final state is never lost ----
  function syncCart() {
    var since = now() - lastSyncAt;
    if (since < SYNC_COOLDOWN_MS) {
      if (!trailingTimer) {
        trailingTimer = setTimeout(function () {
          trailingTimer = null;
          syncCart();
        }, SYNC_COOLDOWN_MS - since);
      }
      return false;
    }
    lastSyncAt = now();
    try {
      if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') {
        window.Rebuy.Cart.fetchCart();
      }
    } catch (e) {}
    return true;
  }

  // ---- UI: never throttled, so an add is never silent ----
  function showSmartCart() {
    try { window.Rebuy.SmartCart.show(); return true; }
    catch (e) { nativeOpen(); return false; }
  }

  // The native fallback is split the same way as the Smart Cart path:
  //   nativeRefresh() = network (theme re-fetches the cart) -> THROTTLED
  //   nativeShow()    = UI (theme opens the drawer)         -> never throttled
  // v2.1: without this split, the backoff path re-dispatched a full cart rebuild
  // for EVERY cart event, which is what kept request volume high.
  function nativeRefresh() {
    var since = now() - lastSyncAt;
    if (since < SYNC_COOLDOWN_MS) {
      if (!trailingTimer) {
        trailingTimer = setTimeout(function () {
          trailingTimer = null;
          nativeRefresh();
        }, SYNC_COOLDOWN_MS - since);
      }
      return false;
    }
    lastSyncAt = now();
    selfDispatch = true;                       // dispatchEvent is synchronous...
    try { document.dispatchEvent(new CustomEvent('cart:build')); }
    finally { setTimeout(function () { selfDispatch = false; }, 0); } // ...release next tick
    return true;
  }

  function nativeShow() {
    selfDispatch = true;
    try { document.dispatchEvent(new CustomEvent('cart:open')); }
    finally { setTimeout(function () { selfDispatch = false; }, 0); }
  }

  function nativeOpen() { nativeRefresh(); nativeShow(); }

  // ---- decide how to reveal the cart ----
  function reveal() {
    if (smartCartReady()) { syncCart(); showSmartCart(); return; }
    if (!rebuyPresent())  { nativeOpen(); return; }
    if (!smartCartPresent()) { nativeOpen(); return; }   // (6) Smart Cart off -> native, no wait
    if (now() < backoffUntil) { nativeOpen(); return; }  // (4) don't hammer a failing SDK
    if (waiting) return;                                 // (1) coalesce

    waiting = true;
    var start = now();
    (function check() {
      if (smartCartReady()) {
        waiting = false;
        syncCart();
        showSmartCart();
        return;
      }
      if (now() - start >= READY_TIMEOUT_MS) {
        waiting = false;
        backoffUntil = now() + SDK_BACKOFF_MS;
        nativeOpen();
        return;
      }
      setTimeout(check, READY_POLL_MS);
    })();
  }

  // Debounce WITH a max wait: rapid events collapse into one reveal, but the cart
  // is still revealed within MAX_DEBOUNCE_WAIT of the first event in a burst — a
  // sustained stream of clicks can never starve the reveal (that would re-create
  // the silent-add symptom this bridge exists to prevent).
  function onCartEvent() {
    if (selfDispatch) return;                  // (2)
    var t = now();
    if (!debounceTimer) burstStartedAt = t;
    clearTimeout(debounceTimer);
    var delay = Math.min(DEBOUNCE_MS, Math.max(0, MAX_DEBOUNCE_WAIT - (t - burstStartedAt)));
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      reveal();
    }, delay);
  }

  // NOTE: deliberately NOT listening for 'cart:build' (see fix 2). In this theme
  // every cart:build dispatcher (closing-snippets, Swym wishlist) also dispatches
  // cart:refresh, so coverage of all add-to-cart paths is unchanged.
  //
  // (6) Rebuy widget buttons add through Rebuy's own Vue handler and dispatch
  // Rebuy's events, never the theme's — so without these two the widget adds
  // were silent. Both fire on a single add; the debounce collapses them.
  ['ajaxProduct:added', 'cart:refresh', 'rebuy:cart.add', 'rebuy.add'].forEach(function (ev) {
    document.addEventListener(ev, onCartEvent);
  });

  // ---- cart-icon click: must always open something (never swallowed) ----
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('.js-drawer-open-cart, .cart-link, [data-rebuy-cart-toggle]');
    if (!trigger) return;
    if (!rebuyPresent()) return;               // Rebuy off -> let the native drawer handle it
    if (!smartCartPresent()) return;           // (6) Smart Cart off -> never swallow the native click
    e.preventDefault();
    e.stopImmediatePropagation();

    if (smartCartReady()) { syncCart(); showSmartCart(); return; }
    if (now() < backoffUntil) { nativeOpen(); return; }
    if (waiting) return;                       // a wait is already running; it will open the cart
    reveal();
  }, true);
})();
